import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSectionV2 } from 'components/UI';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import Images from 'assets/images/imageData';
import localization from 'assets/localization/localization';
import { toast } from 'react-toastify';
import {
  formatDate,
  getRowActions,
  getStatusBadgeClass,
  getStatusLabel,
  getUserRoleIds,
  hasAnyRole,
} from './referralUtils';
import { INITIAL_REFERRAL_FILTERS, PAGE_ACCESS_ROLE_IDS, REFERRAL_FLOW, REFERRAL_FLOW_TABS } from './constants';
import ClientClientReferralWorkflowModal from './ClientClientReferralWorkflowModal';
import '../siteLeadReferral/siteLeadReferral.css';

const cell = (val) => (val != null && val !== '' ? String(val) : '—');
const PAGE_SIZE = 20;

const toApiFilters = (filters, referralFlow) => ({
  ...(filters.status ? { status: filters.status } : {}),
  include_legacy: filters.include_legacy === true,
  referral_flow: referralFlow || REFERRAL_FLOW,
});

const ClientClientReferralList = ({
  referralList = {},
  spocOptions = {},
  rejectReasons = [],
  getReferralList,
  getSpocList,
  getRejectReasons,
  getStatusHistory,
  assignSpoc,
  validateLead,
  rejectLead,
  centralValidate,
  setLeadStage,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [filterData, setFilterData] = useState(INITIAL_REFERRAL_FILTERS);
  const [showFilter, setShowFilter] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modal, setModal] = useState(null);
  const [activeFlow, setActiveFlow] = useState(REFERRAL_FLOW);
  const roleIds = useMemo(() => getUserRoleIds(), []);

  const listData = referralList.referrals || [];
  const currentPage = referralList.page || 1;
  const pageLimit = referralList.limit || PAGE_SIZE;
  const pageCount = Math.ceil((referralList.count || 0) / pageLimit) || 0;

  const loadList = useCallback(async (page, filters, referralFlow = activeFlow) => {
    setIsLoading(true);
    try {
      await getReferralList(page, toApiFilters(filters, referralFlow));
    } catch (err) {
      toast.error(err?.message || 'Failed to load referrals');
    } finally {
      setIsLoading(false);
    }
  }, [getReferralList, activeFlow]);

  useEffect(() => {
    if (hasAnyRole(roleIds, PAGE_ACCESS_ROLE_IDS)) {
      loadList(1, INITIAL_REFERRAL_FILTERS, activeFlow);
    }
  }, [roleIds, loadList, activeFlow]);

  const filterToggle = () => setShowFilter(!showFilter);

  const applyFilter = async (filters) => {
    setFilterData(filters);
    await loadList(1, filters, activeFlow);
    filterToggle();
  };

  const removeFilter = () => {
    setFilterData(INITIAL_REFERRAL_FILTERS);
    loadList(1, INITIAL_REFERRAL_FILTERS, activeFlow);
  };

  const handleFlowTabChange = (flowId) => {
    if (flowId === activeFlow) return;
    setActiveFlow(flowId);
    setFilterData(INITIAL_REFERRAL_FILTERS);
    setModal(null);
    setSelectedRow(null);
  };

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    if (filterData.status) {
      labels.push(`Status: ${getStatusLabel(filterData.status)}`);
    }
    if (filterData.include_legacy) {
      labels.push('Include legacy: Yes');
    }
    return labels;
  }, [filterData]);

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.Filter,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  const openModal = (type, row) => {
    setSelectedRow(row);
    setModal(type);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedRow(null);
  };

  const actionHandlers = {
    onAssignSpoc: (row) => openModal('assign', row),
    onValidate: (row) => openModal('validate', row),
    onReject: (row) => openModal('reject', row),
    onCentralValidate: (row) => openModal('central', row),
    onSetStage: (row) => openModal('stage', row),
    onHistory: (row) => openModal('history', row),
  };

  const tableColumns = [
    { key: 'id', label: 'ID', format: (val) => cell(val) },
    {
      key: 'referred_name',
      label: 'Referred Name',
      format: (_, row) => cell(row.metadata?.referred_name),
    },
    {
      key: 'property_name',
      label: 'Property',
      format: (_, row) => {
        const name = row.flow_data?.property_name;
        if (!name) return '—';
        return (
          <button
            type="button"
            className="site-lead-referral-property-link"
            onClick={() => openModal('flow', row)}
          >
            {name}
          </button>
        );
      },
    },
    {
      key: 'property_type',
      label: 'Type',
      format: (_, row) => cell(row.flow_data?.property_type),
    },
    { key: 'referred_phone', label: 'Phone', format: (val) => cell(val) },
    { key: 'referred_email', label: 'Email', format: (val) => cell(val) },
    {
      key: 'referrer',
      label: 'Referrer',
      format: (_, row) =>
        row.referrer_user_id
          ? `${row.referrer_user_id} (${row.referrer_role || '—'})`
          : '—',
    },
    {
      key: 'referrer_project_id',
      label: 'Project ID',
      format: (val) => cell(val),
    },
    {
      key: 'current_status',
      label: 'Status',
      format: (val) => (
        <span className={`site-lead-referral-badge ${getStatusBadgeClass(val)}`}>
          {getStatusLabel(val)}
        </span>
      ),
    },
    {
      key: 'lead_stage',
      label: 'Stage',
      format: (_, row) => cell(row.flow_data?.lead_stage),
    },
    {
      key: 'region',
      label: 'Region',
      format: (_, row) => cell(row.flow_data?.region),
    },
    {
      key: 'submission_remarks',
      label: 'Remarks',
      format: (val) => cell(val),
    },
    {
      key: 'created_at',
      label: 'Created',
      format: (val) => formatDate(val),
    },
    {
      key: 'actions',
      label: 'Action',
      getActions: (row) => getRowActions(row, roleIds, actionHandlers),
    },
  ];

  if (!hasAnyRole(roleIds, PAGE_ACCESS_ROLE_IDS)) {
    return (
      <Layout title={localization.clientClientReferral} isGoback useNewHeaderLayout>
        <p className="p-3">You do not have access to this page.</p>
      </Layout>
    );
  }

  const srNo = (currentPage - 1) * pageLimit + 1;
  const activeTabLabel =
    REFERRAL_FLOW_TABS.find((tab) => tab.id === activeFlow)?.name ||
    localization.clientClientReferral;

  return (
    <Layout
      title={activeTabLabel}
      Buttons={headerBtns}
      isGoback
      useNewHeaderLayout
    >
      <ul className="nav nav-tabs mb-3 px-3 pt-2" role="tablist">
        {REFERRAL_FLOW_TABS.map((tab) => (
          <li className="nav-item" key={tab.id}>
            <button
              type="button"
              role="tab"
              aria-selected={activeFlow === tab.id}
              className={`nav-link${activeFlow === tab.id ? ' active' : ''}`}
              onClick={() => handleFlowTabChange(tab.id)}
              disabled={isLoading}
            >
              {tab.name}
            </button>
          </li>
        ))}
      </ul>
      <AppliedFiltersBar
        items={appliedFilterLabels}
        onClear={removeFilter}
        clearButtonLabel="Remove filter"
        clearButtonDisabled={isLoading}
      />
      <TableSectionV2
        hideHeader
        hideTitleAndStatusFilter
        className="site-lead-referral-table-section"
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={(val) => loadList(val.selected + 1, filterData, activeFlow)}
        isLoading={isLoading}
        emptyMessage="No referrals found."
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={applyFilter}
        clientClientReferralFilter
        listFilterData={{ appliedFilterValues: filterData }}
      />

      {modal && selectedRow ? (
        <ClientClientReferralWorkflowModal
          action={modal}
          row={selectedRow}
          referralFlow={activeFlow}
          rejectReasons={rejectReasons}
          spocOptions={spocOptions}
          incentiveAmounts={referralList.incentiveAmounts}
          getRejectReasons={getRejectReasons}
          getSpocList={getSpocList}
          getStatusHistory={getStatusHistory}
          assignSpoc={assignSpoc}
          validateLead={validateLead}
          rejectLead={rejectLead}
          centralValidate={centralValidate}
          setLeadStage={setLeadStage}
          onHide={closeModal}
          onSuccess={() => loadList(currentPage, filterData, activeFlow)}
        />
      ) : null}
    </Layout>
  );
};

export default ClientClientReferralList;
